import React, { useMemo, useState } from 'react';
import type { ReceptionistSettingsPageId } from '../../receptionist-settings/types';
import {
  RCPT_SETTINGS_INITIAL_CALL,
  RCPT_SETTINGS_INITIAL_CAPABILITIES,
  RCPT_SETTINGS_INITIAL_CHAT,
  RCPT_SETTINGS_INITIAL_COMPLIANCE,
  RCPT_SETTINGS_INITIAL_EMAIL,
  RCPT_SETTINGS_INITIAL_EMERGENCY,
  RCPT_SETTINGS_INITIAL_KB,
  RCPT_SETTINGS_INITIAL_PERSONALITY,
  RCPT_SETTINGS_INITIAL_PRACTICE_RULES,
  RCPT_SETTINGS_INITIAL_SMS,
} from '../../receptionist-settings/mockData';
import { CapabilitiesSettingsPage } from './CapabilitiesSettingsPage';
import {
  CallSettingsPage,
  ChatSettingsPage,
  EmailSettingsPage,
  SmsSettingsPage,
} from './ChannelSettingsPages';
import { ComplianceSettingsPage } from './ComplianceSettingsPage';
import { EmergencySettingsPage } from './EmergencySettingsPage';
import { KnowledgeBaseSettingsPage } from './KnowledgeBaseSettingsPage';
import { PersonalitySettingsPage } from './PersonalitySettingsPage';
import { PracticeRulesSettingsPage } from './PracticeRulesSettingsPage';
import { ReceptionistSettingsSaveBar } from './ReceptionistSettingsSaveBar';
import { ReceptionistSettingsSubNav } from './ReceptionistSettingsSubNav';
import './receptionist-settings.css';

export interface ReceptionistSettingsViewProps {
  defaultPage?: ReceptionistSettingsPageId;
}

function dirtyCount<T extends object>(current: T, initial: T): number {
  return (Object.keys(initial) as (keyof T)[]).reduce((count, key) => {
    const a = current[key];
    const b = initial[key];
    const same =
      typeof a === 'object' && a !== null
        ? JSON.stringify(a) === JSON.stringify(b)
        : a === b;
    return same ? count : count + 1;
  }, 0);
}

export function ReceptionistSettingsView({ defaultPage = 'practiceRules' }: ReceptionistSettingsViewProps) {
  const [active, setActive] = useState<ReceptionistSettingsPageId>(defaultPage);

  const [personality, setPersonality] = useState(RCPT_SETTINGS_INITIAL_PERSONALITY);
  const [practiceRules, setPracticeRules] = useState(RCPT_SETTINGS_INITIAL_PRACTICE_RULES);
  const [emergency, setEmergency] = useState(RCPT_SETTINGS_INITIAL_EMERGENCY);
  const [knowledge, setKnowledge] = useState(RCPT_SETTINGS_INITIAL_KB);
  const [capabilities, setCapabilities] = useState(RCPT_SETTINGS_INITIAL_CAPABILITIES);
  const [compliance, setCompliance] = useState(RCPT_SETTINGS_INITIAL_COMPLIANCE);
  const [callState, setCallState] = useState(RCPT_SETTINGS_INITIAL_CALL);
  const [smsState, setSmsState] = useState(RCPT_SETTINGS_INITIAL_SMS);
  const [emailState, setEmailState] = useState(RCPT_SETTINGS_INITIAL_EMAIL);
  const [chatState, setChatState] = useState(RCPT_SETTINGS_INITIAL_CHAT);

  const allDirtyCount = useMemo(
    () =>
      dirtyCount(personality, RCPT_SETTINGS_INITIAL_PERSONALITY) +
      dirtyCount(practiceRules, RCPT_SETTINGS_INITIAL_PRACTICE_RULES) +
      dirtyCount(emergency, RCPT_SETTINGS_INITIAL_EMERGENCY) +
      dirtyCount(compliance, RCPT_SETTINGS_INITIAL_COMPLIANCE) +
      dirtyCount(capabilities, RCPT_SETTINGS_INITIAL_CAPABILITIES) +
      dirtyCount(callState, RCPT_SETTINGS_INITIAL_CALL) +
      dirtyCount(smsState, RCPT_SETTINGS_INITIAL_SMS) +
      dirtyCount(emailState, RCPT_SETTINGS_INITIAL_EMAIL) +
      dirtyCount(chatState, RCPT_SETTINGS_INITIAL_CHAT),
    [personality, practiceRules, emergency, compliance, capabilities, callState, smsState, emailState, chatState],
  );

  const isDirty = allDirtyCount > 0;

  const handleSave = () => {
    // mock — would POST to API
  };

  const handleDiscard = () => {
    setPersonality(RCPT_SETTINGS_INITIAL_PERSONALITY);
    setPracticeRules(RCPT_SETTINGS_INITIAL_PRACTICE_RULES);
    setEmergency(RCPT_SETTINGS_INITIAL_EMERGENCY);
    setCompliance(RCPT_SETTINGS_INITIAL_COMPLIANCE);
    setCapabilities(RCPT_SETTINGS_INITIAL_CAPABILITIES);
    setCallState(RCPT_SETTINGS_INITIAL_CALL);
    setSmsState(RCPT_SETTINGS_INITIAL_SMS);
    setEmailState(RCPT_SETTINGS_INITIAL_EMAIL);
    setChatState(RCPT_SETTINGS_INITIAL_CHAT);
  };

  const renderPage = () => {
    switch (active) {
      case 'personality':
        return (
          <PersonalitySettingsPage
            state={personality}
            setState={setPersonality}
            initial={RCPT_SETTINGS_INITIAL_PERSONALITY}
          />
        );
      case 'knowledge':
        return <KnowledgeBaseSettingsPage state={knowledge} setState={setKnowledge} />;
      case 'capabilities':
        return <CapabilitiesSettingsPage state={capabilities} setState={setCapabilities} />;
      case 'emergency':
        return (
          <EmergencySettingsPage
            state={emergency}
            setState={setEmergency}
            initial={RCPT_SETTINGS_INITIAL_EMERGENCY}
          />
        );
      case 'practiceRules':
        return (
          <PracticeRulesSettingsPage
            state={practiceRules}
            setState={setPracticeRules}
            initial={RCPT_SETTINGS_INITIAL_PRACTICE_RULES}
          />
        );
      case 'compliance':
        return (
          <ComplianceSettingsPage
            state={compliance}
            setState={setCompliance}
            initial={RCPT_SETTINGS_INITIAL_COMPLIANCE}
          />
        );
      case 'call':
        return (
          <CallSettingsPage
            state={callState}
            setState={setCallState}
            initial={RCPT_SETTINGS_INITIAL_CALL}
            capState={capabilities}
            setCapState={setCapabilities}
          />
        );
      case 'sms':
        return (
          <SmsSettingsPage
            state={smsState}
            setState={setSmsState}
            initial={RCPT_SETTINGS_INITIAL_SMS}
            capState={capabilities}
            setCapState={setCapabilities}
          />
        );
      case 'email':
        return (
          <EmailSettingsPage
            state={emailState}
            setState={setEmailState}
            initial={RCPT_SETTINGS_INITIAL_EMAIL}
            capState={capabilities}
            setCapState={setCapabilities}
          />
        );
      case 'chat':
        return (
          <ChatSettingsPage
            state={chatState}
            setState={setChatState}
            initial={RCPT_SETTINGS_INITIAL_CHAT}
            capState={capabilities}
            setCapState={setCapabilities}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="set-root flex min-h-full w-full bg-[var(--cream-0)] text-[var(--ink-0)]">
      <ReceptionistSettingsSubNav active={active} onChange={setActive} isDirty={isDirty} />
      <main className="min-w-0 flex-1 overflow-auto px-9 py-8 pb-[120px]">
        <div className="max-w-[1040px]">{renderPage()}</div>
      </main>
      <ReceptionistSettingsSaveBar
        isDirty={isDirty}
        changeCount={allDirtyCount}
        onSave={handleSave}
        onDiscard={handleDiscard}
      />
    </div>
  );
}
